// ControlVitals - Metrics Configuration

export const CORE_METRICS = {
  LCP: {
    name: 'Largest Contentful Paint',
    shortName: 'LCP',
    unit: 'ms',
    good: 2500,
    poor: 4000,
    thresholds: { good: 2500, poor: 4000 },
    description: 'Rychlost načtení: Kdy uživatel vidí hlavní obsah?',
    core: true,
    details: {
      importance: 'LCP měří, kdy uživatel poprvé vidí hlavní vizuální prvek stránky (obrázek, video, velký text). Kritická metrika pro první dojem.',
      measures: 'Měří čas od začátku načítání do vykreslení největšího obsahového elementu ve viewportu.',
      influences: 'Ovlivňuje ji: velikost hero obrázku, rychlost serveru (TTFB), render-blocking CSS/JS.',
      improve: 'Optimalizujte obrázky (WebP/AVIF), použijte preload pro kritické zdroje, zrychlete server.'
    }
  },
  INP: {
    name: 'Interaction to Next Paint',
    shortName: 'INP',
    unit: 'ms',
    good: 200,
    poor: 500,
    thresholds: { good: 200, poor: 500 },
    description: 'Rychlost reakce: Jak rychle web reaguje na kliknutí?',
    core: true,
    details: {
      importance: 'INP měří odezvu webu na uživatelské akce. Pomalé INP = "zamrzlý" web, který nereaguje na kliknutí.',
      measures: 'Měří nejdelší dobu mezi uživatelskou interakcí (klik, tap, klávesa) a následným vykreslením.',
      influences: 'Ovlivňuje ji: dlouho běžící JavaScript, těžké event handlery, blokování hlavního vlákna.',
      improve: 'Rozdělte dlouhé úlohy, použijte debounce/throttle, přesuňte výpočty do Web Workers.'
    }
  },
  CLS: {
    name: 'Cumulative Layout Shift',
    shortName: 'CLS',
    unit: '',
    good: 0.1,
    poor: 0.25,
    thresholds: { good: 0.1, poor: 0.25 },
    description: 'Stabilita: Skáče obsah při načítání?',
    core: true,
    format: (value) => {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return num.toFixed(3);
    },
    details: {
      importance: 'CLS měří vizuální stabilitu stránky. Vysoké CLS = obsah "skáče" a uživatelé klikají na špatná místa.',
      measures: 'Měří kumulativní součet všech neočekávaných posunů layoutu během života stránky.',
      influences: 'Ovlivňují ji: obrázky bez rozměrů, dynamicky načítané fonty, reklamy a embedy.',
      improve: 'Definujte rozměry pro média, rezervujte místo pro reklamy, optimalizujte fonty (font-display: swap).'
    }
  }
};

export const OTHER_METRICS = {
  FCP: {
    name: 'First Contentful Paint',
    shortName: 'FCP',
    unit: 'ms',
    good: 1800,
    poor: 3000,
    thresholds: { good: 1800, poor: 3000 },
    description: 'První obsah: Kdy se něco objeví na obrazovce?',
    core: false,
    details: {
      importance: 'FCP měří, kdy uživatel poprvé vidí jakýkoli obsah (text, obrázek). První signál, že stránka se načítá.',
      measures: 'Měří čas od začátku načítání do prvního vykreslení textu, obrázku nebo SVG na obrazovce.',
      influences: 'Ovlivňuje ji: render-blocking CSS/JS, rychlost serveru, počet externích fontů.',
      improve: 'Inline kritické CSS, použijte async/defer pro skripty, preload fonty.'
    }
  },
  TTFB: {
    name: 'Time to First Byte',
    shortName: 'TTFB',
    unit: 'ms',
    good: 800,
    poor: 1800,
    thresholds: { good: 800, poor: 1800 },
    description: 'Rychlost serveru: Jak dlouho trvá první odpověď?',
    core: false,
    details: {
      importance: 'TTFB měří rychlost serverové odpovědi. Pomalé TTFB zpomaluje všechny ostatní metriky.',
      measures: 'Měří čas od odeslání požadavku do přijetí prvního bajtu odpovědi od serveru.',
      influences: 'Ovlivňuje ji: rychlost serveru, databázové dotazy, síťová latence, CDN konfigurace.',
      improve: 'Optimalizujte server-side logiku, použijte CDN, implementujte cache, zrychlete databázi.'
    }
  },
  FID: {
    name: 'First Input Delay',
    shortName: 'FID',
    unit: 'ms',
    good: 100,
    poor: 300,
    thresholds: { good: 100, poor: 300 },
    description: 'Zpoždění první interakce',
    core: false,
    deprecated: true,
    details: {
      importance: 'FID měří zpoždění první interakce (nahrazeno INP). Historická metrika pro zpětnou kompatibilitu.',
      measures: 'Měří čas od první uživatelské interakce do okamžiku, kdy prohlížeč může zpracovat handler.',
      influences: 'Ovlivňuje ji: těžký JavaScript, dlouhé úlohy, blokování hlavního vlákna.',
      improve: 'Použijte code-splitting, odložte nekritické skripty, minimalizujte main-thread práci.'
    }
  },
  RTT: {
    name: 'Round Trip Time',
    shortName: 'RTT',
    unit: 'ms',
    good: 75,
    poor: 275,
    thresholds: { good: 75, poor: 275 },
    description: 'Síťová odezva: Jak daleko jsou vaši uživatelé?',
    core: false,
    details: {
      importance: 'RTT měří síťovou latenci mezi uživatelem a serverem. Ukazuje geografickou vzdálenost uživatelů.',
      measures: 'Měří čas, který trvá odeslání paketu na server a přijetí odpovědi (round-trip).',
      influences: 'Ovlivňuje ji: fyzická vzdálenost od serveru, kvalita připojení, síťové skoky.',
      improve: 'Použijte CDN s edge lokacemi blíže uživatelům, optimalizujte DNS, použijte HTTP/3.'
    }
  }
};

export const LIGHTHOUSE_METRICS = {
  SI: {
    name: 'Speed Index',
    shortName: 'SI',
    unit: 'ms',
    good: 3400,
    poor: 5800,
    description: 'Rychlost vykreslování',
    core: false,
    lighthouseOnly: true
  },
  TBT: {
    name: 'Total Blocking Time',
    shortName: 'TBT',
    unit: 'ms',
    good: 200,
    poor: 600,
    description: 'Blokování hlavního vlákna',
    core: false,
    lighthouseOnly: true
  },
  TTI: {
    name: 'Time to Interactive',
    shortName: 'TTI',
    unit: 'ms',
    good: 3800,
    poor: 7300,
    description: 'Plná interaktivita',
    core: false,
    lighthouseOnly: true
  }
};

export const EXTENDED_METRICS = {
  FP: { 
    name: 'First Paint', 
    shortName: 'FP',
    unit: 'ms', 
    good: 1000, 
    poor: 2000,
    thresholds: { good: 1000, poor: 2000 },
    description: 'První pixel: Kdy se něco vykreslí?', 
    core: false,
    details: {
      importance: 'FP měří první vizuální změnu na obrazovce (i pozadí). Rychlý feedback pro uživatele.',
      measures: 'Měří čas od začátku načítání do prvního vykreslení jakéhokoliv pixelu.',
      influences: 'Ovlivňuje ji: rychlost serveru, velikost HTML, render-blocking CSS.',
      improve: 'Zlepšíte inline kritickým CSS, minimalizací HTML, zrychlením serveru.'
    }
  },
  DCL: { 
    name: 'DOM Content Loaded', 
    shortName: 'DCL',
    unit: 'ms', 
    good: 1500, 
    poor: 3000,
    thresholds: { good: 1500, poor: 3000 },
    description: 'HTML připraveno: Kdy je dokument načten?', 
    core: false,
    details: {
      importance: 'DCL ukazuje, kdy je HTML zparsované a DOM strom vytvořen. Základ pro JS.',
      measures: 'Měří čas do okamžiku, kdy prohlížeč dokončí parsování HTML (DOMContentLoaded event).',
      influences: 'Ovlivňuje ji: velikost HTML, synchronní skripty, počet externích zdrojů.',
      improve: 'Zlepšíte minimalizací HTML, použitím async/defer pro skripty, redukcí DOM velikosti.'
    }
  },
  ONLOAD: { 
    name: 'Onload', 
    shortName: 'OL',
    unit: 'ms', 
    good: 2500, 
    poor: 5000,
    thresholds: { good: 2500, poor: 5000 },
    description: 'Vše hotovo: Celková doba načtení stránky', 
    core: false,
    details: {
      importance: 'Onload měří kompletní načtení stránky včetně všech zdrojů. Přehled celkové váhy.',
      measures: 'Měří čas do okamžiku window.onload - všechny zdroje (obrázky, CSS, JS) jsou načteny.',
      influences: 'Ovlivňuje ji: celková velikost stránky, počet požadavků, velikost obrázků a médií.',
      improve: 'Snížíte optimalizací obrázků, lazy-loadingem, redukcí nepotřebných zdrojů.'
    }
  }
};

export const LCP_SUBPARTS = {
  timeToFirstByte: {
    name: 'Time to First Byte',
    shortName: 'TTFB (LCP)',
    unit: 'ms',
    good: 800,
    poor: 1800,
    description: 'Doba do prvního bajtu LCP obrázku',
    color: '#3b82f6'
  },
  resourceLoadDelay: {
    name: 'Resource Load Delay',
    shortName: 'Load Delay',
    unit: 'ms',
    good: 100,
    poor: 500,
    description: 'Prodleva před zahájením stahování LCP obrázku',
    color: '#f59e0b'
  },
  resourceLoadTime: {
    name: 'Resource Load Duration',
    shortName: 'Load Time',
    unit: 'ms',
    good: 500,
    poor: 1500,
    description: 'Doba stahování LCP obrázku',
    color: '#22c55e'
  },
  elementRenderDelay: {
    name: 'Element Render Delay',
    shortName: 'Render Delay',
    unit: 'ms',
    good: 100,
    poor: 300,
    description: 'Prodleva vykreslení LCP elementu po stažení',
    color: '#a855f7'
  }
};

export const BIGQUERY_METRICS = EXTENDED_METRICS;

export const METRICS = { ...CORE_METRICS, ...OTHER_METRICS, ...LIGHTHOUSE_METRICS, ...EXTENDED_METRICS };

export const METRICS_CONFIG = METRICS;

export const METRIC_PLAYBOOKS = {
  LCP: {
    title: 'Jak zlepšit LCP (Largest Contentful Paint)',
    why: 'LCP měří, kdy uživatel vidí hlavní obsah stránky. Pomalé LCP způsobuje, že uživatelé odcházejí dřív, než stránku uvidí.',
    checks: [
      'Zkontrolujte velikost hero obrázku nebo videa',
      'Ověřte, že server odpovídá rychle (TTFB)',
      'Hledejte render-blocking CSS a JavaScript'
    ],
    fixes: [
      { action: 'Optimalizujte obrázky', detail: 'Použijte WebP/AVIF formát, správné rozměry, lazy-loading pro obrázky pod ohybem', effort: 'nízká' },
      { action: 'Preload kritických zdrojů', detail: 'Přidejte <link rel="preload"> pro hero obrázky a fonty', effort: 'nízká' },
      { action: 'Odstraňte render-blocking resources', detail: 'Přesuňte CSS do <head>, JavaScript na konec nebo použijte defer/async', effort: 'střední' },
      { action: 'Použijte CDN', detail: 'Servírujte statické soubory z CDN blíže uživatelům', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  INP: {
    title: 'Jak zlepšit INP (Interaction to Next Paint)',
    why: 'INP měří rychlost reakce na uživatelské akce. Pomalé INP znamená "zamrzlý" web, který nereaguje na kliknutí.',
    checks: [
      'Zkontrolujte dlouho běžící JavaScript',
      'Hledejte těžké event handlery',
      'Ověřte, že hlavní vlákno není blokováno',
      'Identifikujte blokující skripty pomocí Long Animation Frames API (LoAF)'
    ],
    fixes: [
      { action: 'Použijte scheduler.postTask()', detail: 'Moderní yielding API pro rozdělení práce s prioritami (user-blocking, background). Taboola dosáhla 36% zlepšení INP.', effort: 'střední' },
      { action: 'Rozdělte dlouhé úlohy', detail: 'Použijte requestIdleCallback nebo setTimeout pro rozdělení práce na menší části', effort: 'střední' },
      { action: 'Debounce/throttle handlery', detail: 'Omezte frekvenci volání event handlerů (scroll, resize, input)', effort: 'nízká' },
      { action: 'Přesuňte práci do Web Workers', detail: 'Náročné výpočty spouštějte mimo hlavní vlákno', effort: 'vysoká' },
      { action: 'Optimalizujte third-party skripty', detail: 'Načítejte analytiku a widgety lazy nebo odložte na interakci', effort: 'střední' },
      { action: 'Implementujte yielding strategii', detail: 'Před render-blocking operacemi vraťte kontrolu hlavnímu vláknu pomocí yield points', effort: 'střední' }
    ],
    owner: 'Frontend',
    caseStudy: {
      company: 'Taboola',
      result: '36% zlepšení INP',
      description: 'Pomocí LoAF API a scheduler.postTask() yielding strategie dosáhli až 36% zlepšení INP.',
      link: 'https://web.dev/case-studies/taboola-inp'
    }
  },
  CLS: {
    title: 'Jak zlepšit CLS (Cumulative Layout Shift)',
    why: 'CLS měří vizuální stabilitu. Vysoké CLS znamená, že obsah "skáče" a uživatelé klikají na špatná místa.',
    checks: [
      'Hledejte obrázky bez definovaných rozměrů',
      'Zkontrolujte dynamicky načítané fonty',
      'Ověřte reklamy a embedy'
    ],
    fixes: [
      { action: 'Definujte rozměry médií', detail: 'Vždy nastavte width a height atributy pro <img> a <video>', effort: 'nízká' },
      { action: 'Rezervujte místo pro reklamy', detail: 'Použijte min-height nebo aspect-ratio pro reklamní sloty', effort: 'nízká' },
      { action: 'Optimalizujte fonty', detail: 'Použijte font-display: swap a preload pro vlastní fonty', effort: 'nízká' },
      { action: 'Vyhněte se vkládání obsahu nad existující', detail: 'Dynamický obsah vkládejte pod fold nebo s animací', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  FCP: {
    title: 'Jak zlepšit FCP (First Contentful Paint)',
    why: 'FCP měří, kdy se objeví první obsah. Pomalé FCP působí, že web "visí" a uživatel neví, jestli funguje.',
    checks: [
      'Zkontrolujte velikost HTML dokumentu',
      'Hledejte render-blocking CSS',
      'Ověřte rychlost serveru (TTFB)'
    ],
    fixes: [
      { action: 'Inline kritické CSS', detail: 'Vložte CSS potřebné pro above-the-fold přímo do <head>', effort: 'střední' },
      { action: 'Minifikujte CSS a HTML', detail: 'Odstraňte zbytečné mezery a komentáře', effort: 'nízká' },
      { action: 'Odstraňte nepoužívané CSS', detail: 'Použijte nástroje jako PurgeCSS pro odstranění mrtvého kódu', effort: 'střední' },
      { action: 'Zlepšete TTFB', detail: 'Optimalizujte server a použijte caching', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  TTFB: {
    title: 'Jak zlepšit TTFB (Time to First Byte)',
    why: 'TTFB měří rychlost serveru. Pomalý TTFB zpomaluje celé načítání - nic se nezobrazí, dokud server neodpoví.',
    checks: [
      'Zkontrolujte dobu zpracování na serveru',
      'Ověřte databázové dotazy',
      'Hledejte pomalé API volání'
    ],
    fixes: [
      { action: 'Povolte caching', detail: 'Použijte HTTP cache headers (Cache-Control, ETag) pro statické zdroje', effort: 'nízká' },
      { action: 'Optimalizujte databázi', detail: 'Přidejte indexy, optimalizujte dotazy, použijte connection pooling', effort: 'střední' },
      { action: 'Použijte CDN', detail: 'Servírujte obsah z edge serverů blíže uživatelům', effort: 'střední' },
      { action: 'Zvažte SSG/ISR', detail: 'Pro statický obsah použijte předgenerované stránky', effort: 'vysoká' }
    ],
    owner: 'Backend/DevOps'
  },
  RTT: {
    title: 'Jak interpretovat RTT (Round Trip Time)',
    why: 'RTT ukazuje kvalitu síťového připojení vašich uživatelů. Vysoké RTT naznačuje geograficky vzdálené nebo pomalé připojení.',
    checks: [
      'Zkontrolujte geografické rozložení uživatelů',
      'Ověřte umístění serverů',
      'Porovnejte RTT podle zařízení (mobil vs desktop)'
    ],
    fixes: [
      { action: 'Použijte CDN s edge lokacemi', detail: 'Zvolte CDN s PoP blízko vašich uživatelů (Cloudflare, Fastly, Akamai)', effort: 'střední' },
      { action: 'Povolte HTTP/2 nebo HTTP/3', detail: 'Moderní protokoly lépe využívají spojení', effort: 'nízká' },
      { action: 'Minimalizujte počet round-trips', detail: 'Spojte soubory, použijte preconnect/dns-prefetch', effort: 'střední' },
      { action: 'Zvažte edge computing', detail: 'Pro dynamický obsah použijte Cloudflare Workers nebo podobné', effort: 'vysoká' }
    ],
    owner: 'DevOps'
  },
  FID: {
    title: 'Jak zlepšit FID (First Input Delay)',
    why: 'FID je starší metrika (nahrazena INP), která měří zpoždění první interakce. Řešení jsou podobná jako u INP.',
    checks: [
      'Zkontrolujte JavaScript spouštěný při načítání',
      'Hledejte blokující skripty třetích stran',
      'Ověřte, že hlavní vlákno není přetížené'
    ],
    fixes: [
      { action: 'Odložte nekritický JavaScript', detail: 'Použijte defer nebo async pro skripty, které nejsou potřeba ihned', effort: 'nízká' },
      { action: 'Rozdělte velké balíčky', detail: 'Použijte code-splitting pro načítání jen potřebného kódu', effort: 'střední' },
      { action: 'Lazy-load third-party widgety', detail: 'Chat widgety, mapy a podobné načítejte až po interakci', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  SI: {
    title: 'Jak zlepšit SI (Speed Index)',
    why: 'Speed Index měří, jak rychle se vizuálně plní obrazovka. Nižší hodnota znamená rychlejší "pocit" načítání.',
    checks: [
      'Analyzujte filmový pás načítání stránky',
      'Hledejte velké prázdné plochy při načítání',
      'Zkontrolujte pořadí načítání obsahu'
    ],
    fixes: [
      { action: 'Optimalizujte critical rendering path', detail: 'Zajistěte, aby above-the-fold obsah byl renderován jako první', effort: 'střední' },
      { action: 'Použijte skeleton screens', detail: 'Zobrazte placeholder obsahu během načítání', effort: 'střední' },
      { action: 'Prioritizujte viditelný obsah', detail: 'Lazy-load obsah pod fold, preload obsah nad fold', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  TBT: {
    title: 'Jak zlepšit TBT (Total Blocking Time)',
    why: 'TBT měří celkový čas, kdy je hlavní vlákno blokováno. Vysoké TBT znamená nereagující stránku.',
    checks: [
      'Identifikujte dlouhé úlohy v DevTools Performance',
      'Hledejte těžké JavaScript knihovny',
      'Zkontrolujte third-party skripty'
    ],
    fixes: [
      { action: 'Minimalizujte práci hlavního vlákna', detail: 'Rozdělte dlouhé úlohy na menší části (<50ms)', effort: 'střední' },
      { action: 'Odstraňte nepoužívaný JavaScript', detail: 'Použijte tree-shaking a code-splitting', effort: 'střední' },
      { action: 'Optimalizujte third-party skripty', detail: 'Načítejte je asynchronně nebo je nahraďte lehčími alternativami', effort: 'střední' }
    ],
    owner: 'Frontend'
  },
  TTI: {
    title: 'Jak zlepšit TTI (Time to Interactive)',
    why: 'TTI měří, kdy je stránka plně interaktivní. Pomalé TTI znamená, že uživatel vidí obsah, ale nemůže s ním pracovat.',
    checks: [
      'Zkontrolujte celkovou velikost JavaScript',
      'Hledejte synchronní operace při inicializaci',
      'Ověřte hydration proces (u SSR frameworků)'
    ],
    fixes: [
      { action: 'Zmenšete JavaScript bundle', detail: 'Analyzujte bundle (webpack-bundle-analyzer) a odstraňte zbytečné závislosti', effort: 'střední' },
      { action: 'Použijte progressive hydration', detail: 'U React/Vue/Svelte hydratujte komponenty postupně', effort: 'vysoká' },
      { action: 'Lazy-load nekritické funkce', detail: 'Načítejte pokročilé funkce až když jsou potřeba', effort: 'střední' }
    ],
    owner: 'Frontend'
  }
};

export const getMetricStatus = (metric, value) => {
  const config = METRICS[metric];
  if (!config || value === null || value === undefined) return 'unknown';

  if (value <= config.good) return 'good';
  if (value <= config.poor) return 'needs-improvement';
  return 'poor';
};

export const formatMetricValue = (metric, value) => {
  const config = METRICS[metric];
  if (!config || value === null || value === undefined) return 'N/A';

  if (config.format) return config.format(value);
  return Math.round(value) + (config.unit ? ` ${config.unit}` : '');
};
